import { Avatar, Box, Divider, Menu } from "@mui/material";
import React, { useState } from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { DotLegalHeader, DotLegalLink, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import LanguageMenu from "../../languageMenu/LanguageMenu.tsx";
import StartPageSelectorDialog from "./startPageSelectorDialog/StartPageSelectorDialog.tsx";

function UserMenu() {
    const { userInitials, fullName, emailAddress, customerName } = useUserContext();
    const { translateString } = useTranslation();
    const { getPurchasePlansUrl, getSignOutUrl } = useUrlProvider();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showHomepageSelectorDialog, setShowHomepageSelectorDialog] = useState(false);

    return (
        <>
            <Box id={"top-bar-user-menu"} sx={{ alignSelf: "center" }}>
                <DotLegalTooltip text={translateString("myPrivacyAccount")} position={"bottom-start"}>
                    <Avatar
                        sx={(theme) => ({
                            border: `1px solid ${theme.palette.primary.main}`,
                            backgroundColor: "#eeeef9",
                            color: theme.palette.primary.main,
                            height: 30,
                            width: 30,
                            fontSize: 14,
                            "&:hover": { cursor: "pointer" },
                        })}
                        onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                        }}
                    >
                        {isNullOrWhitespace(userInitials) ? fullName.charAt(0) : userInitials}
                    </Avatar>
                </DotLegalTooltip>

                <Menu
                    sx={(theme) => ({
                        "& .MuiMenu-paper": {
                            width: 250,
                            padding: 2,
                            marginTop: 1,
                        },
                        "& .MuiList-padding": {
                            padding: 0,
                        },
                        "& .MuiDivider-root": {
                            borderColor: theme.palette.grey[200],
                        },
                    })}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    <DotLegalHeader headerStyle={"small"} marginBottom={0.5}>
                        {fullName}
                    </DotLegalHeader>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                        {emailAddress}
                    </DotLegalHeader>

                    <Divider />

                    <DotLegalHeader headerStyle={"small"} marginBottom={0.5} marginTop={1}>
                        {translateString("account")}
                    </DotLegalHeader>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                        {customerName}
                    </DotLegalHeader>

                    <Divider />

                    <DotLegalHeader headerStyle={"small"} marginBottom={0.5} marginTop={1}>
                        {translateString("language")}
                    </DotLegalHeader>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                        <LanguageMenu />
                    </DotLegalHeader>

                    <Divider />

                    <DotLegalHeader headerStyle={"small"} marginBottom={0.5} marginTop={1}>
                        Plan
                    </DotLegalHeader>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                        <DotLegalLink to={getPurchasePlansUrl()} linkColor={"primary"}>
                            {translateString("seeYourPlan")}
                        </DotLegalLink>
                    </DotLegalHeader>

                    <Divider />

                    <DotLegalHeader headerStyle={"small"} marginBottom={0.5} marginTop={1}>
                        {translateString("startPage")}
                    </DotLegalHeader>
                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={1}>
                        <DotLegalLink
                            onClick={() => {
                                setShowHomepageSelectorDialog(true);
                                setAnchorEl(null);
                            }}
                            linkColor={"primary"}
                        >
                            <Box sx={(theme) => ({ fontSize: 12, fontFamily: theme.typography.fontFamily })}>{translateString("setStartPage")}</Box>
                        </DotLegalLink>
                    </DotLegalHeader>

                    <Divider />

                    <DotLegalHeader headerStyle={"extraSmall"} fontWeight={400} marginBottom={0} marginTop={1}>
                        <DotLegalLink to={getSignOutUrl()} linkColor={"primary"}>
                            {translateString("signOut")}
                        </DotLegalLink>
                    </DotLegalHeader>
                </Menu>
                {showHomepageSelectorDialog && <StartPageSelectorDialog onDialogClose={() => setShowHomepageSelectorDialog(false)} />}
            </Box>
        </>
    );
}

export default UserMenu;
